import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const MIN_PASSWORD = 8;

// Email + password auth for the landing page (MEA-160). Password is the primary
// path; a magic-link fallback is kept for users who prefer no password. On a
// successful password sign-in / sign-up the AuthProvider's onAuthStateChange
// takes over routing, so this form doesn't navigate itself.
export default function AuthForm() {
  const { signInWithPassword, signUpWithPassword, signInWithEmail } = useAuth();
  const [mode, setMode] = useState('signup'); // 'signup' | 'login'
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // null | { type: 'magic' | 'confirm' }
  const [notice, setNotice] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (useMagicLink) {
        const { error } = await signInWithEmail(email);
        if (error) throw error;
        setNotice({ type: 'magic' });
        return;
      }

      if (mode === 'signup') {
        if (password.length < MIN_PASSWORD) {
          throw new Error(`Password must be at least ${MIN_PASSWORD} characters.`);
        }
        const { error, needsConfirmation } = await signUpWithPassword(email, password);
        if (error) throw error;
        if (needsConfirmation) {
          setNotice({ type: 'confirm' });
          return;
        }
        // Session established — routing takes over.
      } else {
        const { error } = await signInWithPassword(email, password);
        if (error) throw error;
        // Session established — routing takes over.
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (notice) {
    return (
      <div className="rounded-panel bg-surface border border-hairline p-6 text-center">
        <h3 className="font-body text-lg font-semibold text-ink mb-2">Check your email</h3>
        <p className="font-body text-sm text-ink-secondary mb-4 leading-relaxed">
          {notice.type === 'magic'
            ? 'We sent a magic link to '
            : 'Confirm your email to finish signing up. We sent a link to '}
          <span className="font-medium text-ink">{email}</span>
        </p>
        <button
          onClick={() => { setNotice(null); setPassword(''); }}
          className="font-body text-sm text-primary hover:text-primary-hover transition-colors"
        >
          Use a different email
        </button>
      </div>
    );
  }

  const inputClass =
    'w-full px-4 py-3 rounded-btn border border-border bg-surface font-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors';

  return (
    <form onSubmit={handleSubmit} className="rounded-panel bg-surface border border-hairline p-6">
      {/* Mode toggle */}
      <div className="flex rounded-btn bg-surface-sunken p-1 mb-5">
        {['signup', 'login'].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setError(null); }}
            className={`flex-1 py-2 rounded-btn font-body text-sm font-medium transition-colors ${
              mode === m ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-ink'
            }`}
          >
            {m === 'signup' ? 'Sign up' : 'Log in'}
          </button>
        ))}
      </div>

      <label htmlFor="email" className="block font-body text-sm font-medium text-ink-secondary mb-1.5">
        Email address
      </label>
      <input
        id="email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className={inputClass}
      />

      {!useMagicLink && (
        <>
          <label htmlFor="password" className="block font-body text-sm font-medium text-ink-secondary mb-1.5 mt-4">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === 'signup' ? `At least ${MIN_PASSWORD} characters` : '••••••••'}
            minLength={MIN_PASSWORD}
            className={inputClass}
          />
          {mode === 'login' && (
            <div className="text-right mt-2">
              <Link to="/reset-password" className="font-body text-xs text-primary hover:text-primary-hover transition-colors">
                Forgot password?
              </Link>
            </div>
          )}
        </>
      )}

      {error && <p className="mt-3 font-body text-sm text-danger">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full mt-5 py-3 rounded-btn bg-primary text-ink-inverse font-body font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting
          ? 'Please wait…'
          : useMagicLink
            ? 'Email me a magic link'
            : mode === 'signup'
              ? 'Start free'
              : 'Log in'}
      </button>

      <button
        type="button"
        onClick={() => { setUseMagicLink((v) => !v); setError(null); }}
        className="w-full mt-4 font-body text-xs text-ink-muted hover:text-ink-secondary transition-colors text-center"
      >
        {useMagicLink ? 'Use a password instead' : 'Or email me a magic link (no password)'}
      </button>
    </form>
  );
}
